(() => {
  if (window.__roundTrackerFourPackInitialized) return;
  window.__roundTrackerFourPackInitialized = true;

  const ROOT_SELECTOR = 'variant-selects[data-product-handle="round-tracker"]';
  const BUILDER_SELECTOR = '[data-round-tracker-four-pack]';
  const MAX_ITEMS = 4;
  const states = new Map();

  const normalize = (value) => String(value || '').trim();
  const getProductInfo = (element) => element?.closest?.('product-info');
  const getSectionKey = (root) => normalize(root?.dataset.section || 'main');

  function getState(root) {
    const key = getSectionKey(root);
    if (!states.has(key)) states.set(key, new Map());
    return states.get(key);
  }

  function getSelectedInput(root, optionName) {
    return Array.from(root?.querySelectorAll?.('input[data-option-name]:checked') || []).find(
      (input) => normalize(input.dataset.optionName).toLowerCase() === optionName.toLowerCase()
    );
  }

  function isFourPack(root) {
    return /^4\s+tracker$/i.test(normalize(getSelectedInput(root, 'quantity')?.value));
  }

  function getChoices(root) {
    return Array.from(root.querySelectorAll('[data-round-tracker-mix-choice]'));
  }

  function getChoiceInput(choice) {
    return choice?.querySelector('input[data-option-name="Color"]');
  }

  function isChoiceAvailable(input) {
    return Boolean(
      input &&
        !input.disabled &&
        !input.classList.contains('disabled') &&
        !input.classList.contains('visually-disabled')
    );
  }

  function getTotal(state) {
    return Array.from(state.values()).reduce((total, count) => total + count, 0);
  }

  function getSummary(state) {
    return Array.from(state.entries())
      .filter(([, count]) => count > 0)
      .map(([name, count]) => `${name} x${count}`)
      .join(', ');
  }

  function isSelectedVariantAvailable(root) {
    try {
      return Boolean(JSON.parse(root.querySelector('[data-selected-variant]')?.textContent || '{}').available);
    } catch (_error) {
      return true;
    }
  }

  function setSubmitAvailability(root, active, complete) {
    const productInfo = getProductInfo(root);
    const sectionId = productInfo?.dataset.section;
    if (!productInfo || !sectionId) return;

    const variantAvailable = isSelectedVariantAvailable(root);
    const enabled = variantAvailable && (!active || complete);
    const submitButton = productInfo.querySelector(`#ProductSubmitButton-${CSS.escape(sectionId)}`);
    const stickyButton = productInfo.querySelector('[data-mobile-sticky-submit]');
    [submitButton, stickyButton].filter(Boolean).forEach((button) => {
      if (active) {
        button.dataset.roundTrackerMixGate = 'true';
        button.disabled = !enabled;
        button.setAttribute('aria-disabled', String(!enabled));
      } else if (button.dataset.roundTrackerMixGate === 'true') {
        button.disabled = !variantAvailable;
        button.setAttribute('aria-disabled', String(!variantAvailable));
        delete button.dataset.roundTrackerMixGate;
      }
    });

    const dynamicCheckout = productInfo.querySelector('.shopify-payment-button');
    if (dynamicCheckout) {
      if (active && !complete && !dynamicCheckout.hidden) {
        dynamicCheckout.dataset.roundTrackerHiddenByMix = 'true';
        dynamicCheckout.hidden = true;
      } else if ((!active || complete) && dynamicCheckout.dataset.roundTrackerHiddenByMix === 'true') {
        dynamicCheckout.hidden = false;
        delete dynamicCheckout.dataset.roundTrackerHiddenByMix;
      }
    }
  }

  function syncCarrierVariant(root, colorValue) {
    const input = Array.from(root.querySelectorAll('input[data-option-name="Color"]')).find(
      (candidate) => normalize(candidate.value) === colorValue && isChoiceAvailable(candidate)
    );
    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function refresh(root) {
    const builder = root.querySelector(BUILDER_SELECTOR);
    if (!builder) return;

    const active = isFourPack(root);
    const state = getState(root);
    const total = getTotal(state);
    const complete = active && total === MAX_ITEMS;
    const property = builder.querySelector('[data-round-tracker-four-pack-property]');
    const totalElement = builder.querySelector('[data-round-tracker-mix-total]');
    const summary = builder.querySelector('[data-round-tracker-four-pack-summary]');

    root.classList.toggle('is-building-four-pack', active);
    builder.hidden = !active;

    getChoices(root).forEach((choice) => {
      const input = getChoiceInput(choice);
      if (!input) return;

      const count = state.get(normalize(input.value)) || 0;
      const addButton = choice.querySelector('[data-round-tracker-mix-add]');
      const removeButton = choice.querySelector('[data-round-tracker-mix-remove]');
      const countElement = choice.querySelector('[data-round-tracker-mix-count]');

      if (addButton) addButton.disabled = !active || !isChoiceAvailable(input) || total >= MAX_ITEMS;
      if (removeButton) {
        removeButton.hidden = !active;
        removeButton.disabled = !active || count === 0;
      }
      if (countElement) {
        countElement.hidden = !active || count === 0;
        countElement.textContent = String(count);
      }
    });

    if (totalElement) totalElement.textContent = String(total);
    if (summary) {
      summary.textContent = total
        ? `${getSummary(state)}${complete ? '' : ` - add ${MAX_ITEMS - total} more`}`
        : 'Tap + beside any item to add it.';
    }
    if (property) {
      property.value = complete ? getSummary(state) : '';
      property.disabled = !complete;
    }

    setSubmitAvailability(root, active, complete);
  }

  function changeCount(button, amount) {
    const root = button.closest(ROOT_SELECTOR);
    const choice = button.closest('[data-round-tracker-mix-choice]');
    const input = getChoiceInput(choice);
    if (!root || !input || !isFourPack(root)) return;

    const state = getState(root);
    const name = normalize(input.value);
    const current = state.get(name) || 0;
    const total = getTotal(state);

    if (amount > 0 && total < MAX_ITEMS) {
      state.set(name, current + 1);
      if (total === 0) syncCarrierVariant(root, name);
    } else if (amount < 0 && current > 0) {
      if (current === 1) state.delete(name);
      else state.set(name, current - 1);

      if (input.checked && !state.has(name) && state.size) {
        syncCarrierVariant(root, state.keys().next().value);
      }
    }

    refresh(root);
  }

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest?.('[data-round-tracker-mix-add]');
    const removeButton = event.target.closest?.('[data-round-tracker-mix-remove]');
    const button = addButton || removeButton;
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    changeCount(button, addButton ? 1 : -1);
  });

  document.addEventListener('change', (event) => {
    const root = event.target.closest?.(ROOT_SELECTOR);
    if (!root) return;
    window.requestAnimationFrame(() => refresh(root));
  });

  document.addEventListener(
    'submit',
    (event) => {
      const productInfo = event.target.closest?.('product-info');
      const root = productInfo?.querySelector?.(ROOT_SELECTOR);
      if (!root || !isFourPack(root) || getTotal(getState(root)) === MAX_ITEMS) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      refresh(root);
      root.querySelector(BUILDER_SELECTOR)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    true
  );

  function refreshAll(scope = document) {
    scope?.querySelectorAll?.(ROOT_SELECTOR).forEach(refresh);
  }

  document.addEventListener('DOMContentLoaded', () => refreshAll());
  document.addEventListener('product-info:loaded', (event) => refreshAll(event.target));
  document.addEventListener('shopify:section:load', (event) => refreshAll(event.target));

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const productInfo = document.querySelector(
        `product-info[data-section="${CSS.escape(data.sectionId)}"]`
      );
      window.requestAnimationFrame(() => refreshAll(productInfo || document));
    });
  }

  if (document.readyState !== 'loading') refreshAll();
})();
